import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { Switcher, Title } from "../../Atoms";
import { Button } from "@chakra-ui/react";
import { DownloadIcon } from "../../../Assets/images/svgs";
import { ReactTable } from "../../Molecules";
import { getOrderDetails, getShipmentDetails } from "../../../APIs";
import { getDate, getLocalisedDate } from "../../../Library/datetime";
import { getLocalisedNumber } from "../../../Library/helpers";
import { SHOW_FILTERED_ORDER_HEADERS, SHOW_ORDER_HEADERS, SHOW_SHIPMENT_HEADERS } from "../../../Library/tableHeaders";
import { getFilters } from "../../../Library/tableHelpers";
import { Link } from "react-router-dom";
import { ORDER_STATUS, ORDER_STATUS_VAL, SELLER_DES, SHIPMENT_STATUS, SHIPPING_STATUSES, SUBDEALERS_DES } from "../../../Library/constants";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";
import PATHS from "../../../Routes/paths"

const OrdersTable = ({ filterHeaders = false, dealerId, hideDealerNames = false, title, shipment, des, allOrdersData, shipmentData }) => {
  const [ orders, setOrders ] = useState([]);
  const [ selectedTableFilter, setSelectedTableFilter ] = useState(1);
  const ordersCountAsPerStatus = useRef(shipment ? { 1: [], 2: [], 3: [], total: [] } : { 1: [], 1.1:[], 2: [], 3: [], 4: [], 5: [], total: [] });
  const DESIGNATION = JSON.parse(window.localStorage.getItem("des"));

  const processAndStoreData = (data, isShipment) => {
    if (!data.orders || data.orders.length === 0) return console.error("no orders found");

    const orders = data.orders;
    const STATUS = isShipment ? SHIPMENT_STATUS : ORDER_STATUS;

    orders.forEach(order => {
      const { os, amt, cdt } = order;

      if (os && os.length > 0) {
        const latestStatus = os.filter(stat => stat.ts).pop();
        if (latestStatus) {
          const { stat } = latestStatus;
          order.currentState = STATUS[stat];
          order.state = stat;

          if (!ordersCountAsPerStatus.current[stat]) {
            ordersCountAsPerStatus.current[stat] = [];
          }
          ordersCountAsPerStatus.current[stat].push(order);
        }
      }

      if (amt) order.amt = getLocalisedNumber(amt);
      if (cdt) order.cdt = getLocalisedDate(cdt, true);
      order.vnm = "Ajjas";
    });

    ordersCountAsPerStatus.current.total = orders;
    setOrders(orders);
  }


  useEffect(() => {
    const handleData = (data, processFunction) => {
      if (data && data.orders && data.orders.length) {
        processFunction(data);
      } else {
        const callback = (err, fetchedData) => {
          if (err) console.error(err);
          else processFunction(fetchedData);
        };

        if (shipment) getShipmentDetails(null, dealerId || null, null, callback)
        else getOrderDetails(null, dealerId || null, null, callback);
      }
    };

    if (shipment) handleData(shipmentData, (data) => processAndStoreData(data, true));
    else handleData(allOrdersData, (data) => processAndStoreData(data, false));
  }, [dealerId, shipment, allOrdersData, shipmentData]);


  const columns = useMemo(() => {
    let TABLE_HEADERS = SHOW_ORDER_HEADERS
    if (filterHeaders) TABLE_HEADERS = SHOW_FILTERED_ORDER_HEADERS;
    if (shipment) TABLE_HEADERS = SHOW_SHIPMENT_HEADERS;

    const cols = [];
    Object.values(TABLE_HEADERS).forEach(header => {
      if (hideDealerNames && header.key === "dnm") return;

      cols.push({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        ...getFilters(header),
        Cell: (row) => {
          if (row.column.id === "dnm") {
            return <span className="table-cell-nowrap">{row.value}</span>
          }

          if (row.column.id === "orderId") {
            const getPath = (shipment, DESIGNATION, row) => {
              const basePath = shipment ? PATHS.SHIPMENT_OVERVIEW : PATHS.DEALERS_OVERVIEW;
              const orderPath = DESIGNATION !== SUBDEALERS_DES ? basePath : "";
              const detailPath = `${orderPath}${PATHS.ORDERS_OVERVIEW}/${PATHS.ORDER_DETAIL}?`;
              const queryParam = shipment ? `shipId=${row.value}` : `idx=${row.value}`;

              return `${detailPath}${queryParam}`;
            };

            return (
              <Link className="order-id-cell-value" to={getPath(shipment, DESIGNATION, row)}>
                  {row.value || '-'}
              </Link>
            );
          }

          if (row.column.id === "transc") {
            return (row.value && row.value.amt) || "-"
          }
          return row.value
        },
      });
    });

    return cols;
  }, [filterHeaders, hideDealerNames]);

  const TABLE_FILTER_OPTIONS = [
    { idx: 1, dataKey: "total", label: `All (${ordersCountAsPerStatus.current["total"].length})` },
    { idx: 2, dataKey: "1", label: `Placed (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Order Placed"]].length})` },
    { idx: 3, dataKey: "1.1", label: `PI Raised (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["PI Raised"]]?.length})` },
    { idx: 4, dataKey: "2", label: `Cancelled (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Order Cancelled"]].length})` },
    { idx: 5, dataKey: "3", label: `Payment Done (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Payment Done"]].length})`, spacing: true },
    { idx: 6, dataKey: "4", label: `Accepted (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Order Accepted"]]?.length})` },
    { idx: 7, dataKey: "5", label: `Added In Stock (${ordersCountAsPerStatus.current[ORDER_STATUS_VAL["Device Added In Stock"]]?.length})`, spacing: true },
  ]

  const SHIPMENT_FILTER_OPTIONS = [
    { idx: 1, dataKey: "total", label: `All (${ordersCountAsPerStatus.current["total"].length})` },
    { idx: 2, dataKey: "1", label: `Requested (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Shipment Requested"]].length})` },
    { idx: 3, dataKey: "2", label: `Shipped (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Shipped"]].length})` },
    { idx: 4, dataKey: "3", label: `Delivered (${ordersCountAsPerStatus.current[SHIPPING_STATUSES["Delivered"]].length})` },
  ]

  const handleFiltersTabChange = (tab) => {
    setSelectedTableFilter(tab.idx);
    setOrders(ordersCountAsPerStatus.current[tab.dataKey]);
  }

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((order, idx) => {
      order["Sr No"] = idx + 1;
      order["Order ID"] = order.orderId || "-";
      order["Order Date"] = getDate(order.cdt, null, "/") || "-";
      order["Order Status"] = order.currentState || "-";
      order["Quantity"] = order.devcnt || "-";
      order["Dealer"] = order.dnm || "-";
      order["Vendor"] = order.vnm || "-";
      order["IMEIs"] = (order.imeis && order.imeis.length) ? order.imeis.join(", ") : "-";
      order["Amount"] = (!shipment && order.transc && order.transc.amt) ? order.transc.amt : "-";
      order["Placed"] = !shipment && order.os[0] && order.os[0].ts ? getDate(order.os[0].ts, true) : "-";
      order["Accepted"] = !shipment && order.os[1] && order.os[1].ts ? getDate(order.os[1].ts, true) : "-";
      order["Cancelled"] = !shipment && order.os[2] && order.os[2].ts ? getDate(order.os[2].ts, true) : "-";
      order["Shipped"] = !shipment && order.os[3] && order.os[3].ts ? getDate(order.os[3].ts, true) : "-";
      order["Delivered"] = !shipment && order.os[4] && order.os[4].ts ? getDate(order.os[4].ts, true) : "-";
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Order ID, Order Date, Order Status, Quantity, Dealer, Vendor, IMEIs, Amount, Placed, Accepted, Cancelled, Shipped, Delivered";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Orders_" + Date.now(), false);
  }

  return (
    <section className="orders-table">
      <article className="order-details-table-container">
        <Title variant="md" fixed>{title || (DESIGNATION === SUBDEALERS_DES ? "Shipment Details" : "Order Details")}</Title>
        <div className="order-table-filters-row">
          <Switcher
            className="table-filters-switcher"
            options={shipment ? SHIPMENT_FILTER_OPTIONS : TABLE_FILTER_OPTIONS}
            selectedOption={selectedTableFilter}
            handleChange={handleFiltersTabChange}
          />

          <Button onClick={() => downloadData(orders)} className="download-order-btn">
            <DownloadIcon />
          </Button>
        </div>
        <ReactTable
          columns={columns}
          data={orders}
          showSearch={true}
          pageSize={10}
        />
      </article>
    </section>
  )
}

export default OrdersTable
